import React from 'react'
import { useSchoolSearchMutation } from '@mote/api'
import { isNumeric, useQueryParams } from '@mote/helpers'
import { listPaginatedSchools, listSchools } from './adminHelpers'

export const DEFAULT_STATE = {
  mode: 'list',
  showAll: false,
  searchTerm: '',
  error: null,
  loading: true,
  pageIndex: 0,
  pageIndexes: [],
  institutions: [],
  allInstitutions: [],
  paginatedInstitutions: [],
  embedModal: {
    open: false,
    content: '',
    institutionId: '',
    institutionIndex: -1
  }
}

export const AdminSchoolsContext = React.createContext({})

export const AdminSchoolsProvider = ({ children }) => {
  const [state, setState] = React.useState(DEFAULT_STATE)
  const { queryParams, setQueryParams } = useQueryParams()
  const { mutate: searchSchools } = useSchoolSearchMutation()

  const updateState = React.useCallback(states => {
    setState(prev => ({ ...prev, ...states }))
  }, [])

  const refreshSchools = React.useCallback(() => {
    if (state.mode === 'list') {
      updateState({ loading: true, searchTerm: '' })

      listSchools(
        data => {
          updateState({
            institutions: data.institutions,
            allInstitutions: data.institutions,
            loading: false
          })
        },
        error => {
          updateState({
            loading: false,
            error: error?.toString() || 'Something went wrong'
          })
        }
      )
    }
  }, [state])

  const refreshSchoolsPaginated = React.useCallback(
    (pageIndex = 0) => {
      updateState({ loading: true, searchTerm: '' })

      setQueryParams({ page: pageIndex })

      listPaginatedSchools(
        pageIndex,
        data => {
          updateState({
            institutions: data.institutions.currentPage,
            paginatedInstitutions: data.institutions.currentPage,
            pageIndex: data.institutions.currentPageIndex,
            pageIndexes: data.institutions.pageIndexes,
            loading: false
          })
        },
        error => {
          updateState({
            loading: false,
            error: error?.toString() || 'Something went wrong'
          })
        }
      )
    },
    [state]
  )

  const resetState = React.useCallback(() => {
    setState(DEFAULT_STATE)
    refreshSchoolsPaginated()
  }, [refreshSchoolsPaginated])

  const lightRefreshSchools = React.useCallback(loadingKey => {
    listSchools(
      data => {
        const state = {
          institutions: data.institutions
        }

        if (loadingKey) state[loadingKey] = false
        updateState(state)
      },
      () => {
        if (loadingKey) updateState({ [loadingKey]: false })
      }
    )
  }, [])

  const handleSchoolSearch = React.useCallback(
    async searchTerm => {
      updateState({ loading: true })

      const previousInstitutions = state.showAll
        ? state.allInstitutions
        : state.paginatedInstitutions

      if (!searchTerm) {
        updateState({
          institutions: previousInstitutions,
          loading: false
        })

        return
      }

      try {
        const response = await searchSchools({ variables: { searchTerm } })

        if (!response || response.error || !response.data) {
          throw new Error('Something went wrong')
        }

        const newInstitutions = response.data.search.matches

        updateState({
          institutions: newInstitutions,
          loading: false
        })
      } catch (error) {
        console.error(error)
        updateState({ loading: false })
      }
    },
    [state]
  )

  React.useEffect(() => {
    if (queryParams.showAll === 'true') {
      updateState({ showAll: true })
      refreshSchools()
      return
    }

    const hasPageIndex = isNumeric(queryParams.page)

    if (!hasPageIndex) {
      setQueryParams({ page: 0 })
    }

    const index = hasPageIndex ? parseInt(queryParams.page) : 0

    refreshSchoolsPaginated(index)
  }, [])

  return (
    <AdminSchoolsContext.Provider
      value={{
        state,
        updateState,
        resetState,
        refreshSchools,
        refreshSchoolsPaginated,
        lightRefreshSchools,
        searchSchools: handleSchoolSearch
      }}
    >
      {children}
    </AdminSchoolsContext.Provider>
  )
}

export const useAdminSchools = () => {
  const context = React.useContext(AdminSchoolsContext)

  if (context === null) {
    throw new Error(
      'Attempted to call useAdminSchools outside the AdminSchools component.'
    )
  }

  return context
}
